import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  House,
  Notebook,
  CalendarBlank,
  UsersThree,
  Files,
  ListChecks,
  Bell,
  ChartBar,
  Sparkle,
  Gear,
  SignOut,
  MagnifyingGlass,
  DotsThreeCircle,
  X,
} from "@phosphor-icons/react";
import QuickCapture from "@/components/QuickCapture";
import { InstallMoreRow } from "@/components/InstallPWA";

// Full site nav — desktop sidebar shows every item. Mobile bottom bar shows
// the four most-used tabs + a "More" trigger that reveals the rest.
const nav = [
  { to: "/dashboard", label: "Dashboard", icon: House, testid: "nav-dashboard", end: true },
  { to: "/log", label: "Daily Log", icon: Notebook, testid: "nav-log" },
  { to: "/calendar", label: "Calendar", icon: CalendarBlank, testid: "nav-calendar" },
  { to: "/search", label: "Search", icon: MagnifyingGlass, testid: "nav-search" },
  { to: "/people", label: "People", icon: UsersThree, testid: "nav-people" },
  { to: "/documents", label: "Documents", icon: Files, testid: "nav-documents" },
  { to: "/followups", label: "Follow-ups", icon: ListChecks, testid: "nav-followups" },
  { to: "/reminders", label: "Reminders", icon: Bell, testid: "nav-reminders" },
  { to: "/reports", label: "Reports", icon: ChartBar, testid: "nav-reports" },
  { to: "/assistant", label: "AI Assistant", icon: Sparkle, testid: "nav-assistant" },
  { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings" },
];

// The four tabs pinned on the mobile bottom bar. Order tuned for daily use:
// Dashboard is home, Log is capture, Assistant is where the value happens,
// Search is triage. Everything else lives under "More".
const MOBILE_TABS = [
  { to: "/dashboard", short: "Home" },
  { to: "/log", short: "Log" },
  { to: "/assistant", short: "Assistant" },
  { to: "/search", short: "Search" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const onLogout = async () => { await logout(); navigate("/login"); };

  // Close the More sheet on any route change so tapping a link inside dismisses it.
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  // Lock body scroll while the More sheet is open (iOS Safari friendly).
  useEffect(() => {
    if (!moreOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [moreOpen]);

  const primaryTabs = MOBILE_TABS.map((t) => {
    const item = nav.find((n) => n.to === t.to);
    return item ? { ...item, short: t.short } : null;
  }).filter(Boolean);
  const moreItems = nav.filter((n) => !MOBILE_TABS.some((t) => t.to === n.to));

  return (
    <div className="App min-h-screen bg-[#F9FAFB] text-slate-900">
      {/* ---- Sidebar (desktop) ---- */}
      <aside className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:w-64 bg-white border-r border-slate-200/70 z-30">
        <div className="px-6 pt-7 pb-5">
          <div className="flex items-center gap-3" data-testid="brand-logo-sidebar">
            <img src="/al-axe-brand.png" alt="AL-AXE — Intelligent Operations" className="h-10 w-auto" />
          </div>
        </div>
        <nav className="px-3 flex-1 space-y-1 scrollbar-thin overflow-y-auto">
          {nav.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <Icon size={18} weight="duotone" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200/70">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full al-gradient text-white flex items-center justify-center font-semibold text-sm">
              {(user?.name || "U").slice(0, 1)}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={onLogout} className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-900 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* ---- Mobile top bar ---- */}
      <header className="md:hidden sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-slate-200/60 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center" data-testid="brand-logo-mobile">
          <img src="/al-axe-brand.png" alt="AL-AXE" className="h-7 w-auto" />
        </div>
        <button data-testid="mobile-logout-btn" onClick={onLogout} className="text-slate-500 p-2 -m-2" aria-label="Sign out">
          <SignOut size={20} />
        </button>
      </header>

      {/* ---- Content ---- */}
      <main
        className="md:pl-64 md:pb-10"
        style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
          <Outlet />
        </div>
      </main>

      {/* ---- Mobile bottom tab bar ---- */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 backdrop-blur-xl bg-white/95 border-t border-slate-200/70 z-30"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="grid grid-cols-5 text-xs">
          {primaryTabs.map(({ to, short, icon: Icon, testid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`m-${testid}`}
              className={({ isActive }) => `flex flex-col items-center py-2.5 gap-0.5 min-h-[52px] justify-center ${isActive ? "text-slate-900" : "text-slate-500"}`}
            >
              <Icon size={22} weight="duotone" />
              <span className="text-[10px]">{short}</span>
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            data-testid="m-nav-more"
            aria-label="More"
            aria-expanded={moreOpen}
            className={`flex flex-col items-center py-2.5 gap-0.5 min-h-[52px] justify-center transition-colors ${moreOpen ? "text-slate-900" : "text-slate-500"}`}
          >
            <DotsThreeCircle size={22} weight="duotone" />
            <span className="text-[10px]">More</span>
          </button>
        </div>
      </nav>

      {/* ---- Mobile More sheet ---- */}
      {moreOpen && (
        <div
          data-testid="mobile-more-sheet"
          className="md:hidden fixed inset-0 z-50 bg-slate-900/45 backdrop-blur-sm flex items-end"
          onClick={() => setMoreOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full bg-white rounded-t-3xl shadow-2xl animate-[slideup_.18s_ease-out]"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Navigate</div>
                <div className="text-lg font-semibold tracking-tight">More</div>
              </div>
              <button
                onClick={() => setMoreOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-more-close"
                className="w-9 h-9 rounded-full hover:bg-slate-100 grid place-items-center text-slate-600"
              >
                <X size={18} />
              </button>
            </div>
            <ul className="px-3 pb-3">
              {moreItems.map(({ to, label, icon: Icon, testid, end }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={end}
                    data-testid={`more-${testid}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3.5 rounded-xl text-sm font-medium min-h-[52px] ${isActive ? "bg-slate-900 text-white" : "text-slate-800 hover:bg-slate-100 active:bg-slate-100"}`
                    }
                  >
                    <Icon size={20} weight="duotone" />
                    {label}
                  </NavLink>
                </li>
              ))}
              <li className="mt-2 pt-2 border-t border-slate-100">
                <InstallMoreRow onDone={() => setMoreOpen(false)} />
              </li>
              <li>
                <button
                  onClick={onLogout}
                  data-testid="more-logout-btn"
                  className="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl text-sm font-medium min-h-[52px] text-slate-600 hover:bg-slate-100"
                >
                  <SignOut size={20} /> Sign out
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}

      <QuickCapture />
    </div>
  );
}
